/**
 * Lookup page — the console index.
 *
 * One prominent search box accepting any of the four operator identifiers
 * (email, acct_…, usr_…, user_…). Submit runs GET /v1/admin/lookup; the
 * classifier in LookupPolicies.ts drives an inline hint ONLY (the server
 * validates). States: initial empty prompt, loading skeleton, no-match notice,
 * error (DIRECT-011 envelope message + request id), and the match card with a
 * prominent "Open account →" continuation.
 */
import { useState, type FormEvent } from 'react';
import { isApiError } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search } from '@/components/icons';
import { PageContainer } from '@/components/navigation/PageContainer';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { useAdminLookup } from './hooks/useAdminLookup';
import { classifyQuery } from './LookupPolicies';
import { LookupMatchCard } from './LookupMatchCard';

const PLACEHOLDER = 'email, acct_…, usr_…, or user_… (Clerk) id';

export function LookupPage() {
  const [query, setQuery] = useState('');
  const lookup = useAdminLookup();

  const classification = classifyQuery(query);
  const showHint = query.trim().length > 0 && !lookup.isPending;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const q = query.trim();
    if (q.length === 0 || lookup.isPending) return;
    lookup.search(q).catch(() => {
      // Rejection is handled through lookup.isError below; the thrown ApiError
      // must not escape the event handler.
    });
  }

  return (
    <PageContainer>
      <PageHeader
        title="Lookup"
        description="Find an account by email address, account id, or user id, then inspect the full subject record."
      />

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row" data-testid="lookup-form">
            <label htmlFor="admin-lookup-query" className="sr-only">
              Search query
            </label>
            <Input
              id="admin-lookup-query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={PLACEHOLDER}
              autoComplete="off"
              spellCheck={false}
              className="h-10 font-mono text-sm"
              data-testid="lookup-query-input"
              aria-describedby="admin-lookup-hint"
            />
            <Button type="submit" disabled={lookup.isPending || query.trim().length === 0} data-testid="lookup-submit">
              <Search aria-hidden="true" />
              Search
            </Button>
          </form>
          {/* Client-side hint from the pure classifier. Cosmetic only — the
              server re-validates and remains the authority. */}
          <p
            id="admin-lookup-hint"
            aria-live="polite"
            className="mt-2 min-h-5 text-xs text-muted-foreground"
            data-testid="lookup-hint"
          >
            {showHint ? classification.hint : 'Search by email, account id (acct_…), internal user id (usr_…), or Clerk user id (user_…).'}
          </p>
        </CardContent>
      </Card>

      {/* Loading skeleton. */}
      {lookup.isPending ? (
        <Card data-testid="lookup-loading">
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </CardContent>
        </Card>
      ) : null}

      {/* Error: the server's envelope message, request id, retry. */}
      {!lookup.isPending && lookup.isError ? (
        <div data-testid="lookup-error">
          <ErrorState
            title="Lookup failed"
            error={lookup.error}
            description={isApiError(lookup.error) ? undefined : 'The admin API request failed.'}
            onRetry={() => {
              const q = query.trim();
              if (q) lookup.search(q).catch(() => {});
            }}
          />
        </div>
      ) : null}

      {/* No match: a miss is a valid answer, not a failure. */}
      {!lookup.isPending && !lookup.isError && lookup.data && lookup.data.match === null ? (
        <Card data-testid="lookup-no-match">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No account found — check the address or try the account/Clerk id.
          </CardContent>
        </Card>
      ) : null}

      {/* Match card with the open-account continuation. */}
      {!lookup.isPending && !lookup.isError && lookup.data?.match ? (
        <LookupMatchCard match={lookup.data.match} />
      ) : null}

      {/* Initial empty state (no search yet). */}
      {!lookup.isPending && !lookup.data && !lookup.isError ? (
        <p className="text-sm text-muted-foreground" data-testid="lookup-empty">
          Enter a query above to resolve a support subject.
        </p>
      ) : null}
    </PageContainer>
  );
}
